import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { INVESTOR_NAV_LABEL } from '@workspace/seedforge-ui/lib/investor-nav'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { AssistantGate } from '../../../../features/investor-assistant/components/assistant-gate'
import {
  type MatchedPassage,
  StartupSidePanel,
} from '../../../../features/investor-assistant/components/relationship/startup-side-panel'
import {
  lastView,
  rememberView,
} from '../../../../features/investor-assistant/components/remembered-view'
import {
  BrowseSection,
  type SearchChip,
} from '../../../../features/investor-assistant/components/search/browse-section'
import { ConnectAiLink } from '../../../../features/investor-assistant/components/search/connect-ai-link'
import { PhraseSearch } from '../../../../features/investor-assistant/components/search/phrase-search'
import { RecentSearches } from '../../../../features/investor-assistant/components/search/recent-searches'
import { WatchdogTab } from '../../../../features/investor-assistant/components/search/watchdog-tab'
import { copy } from '../../../../features/investor-assistant/copy/investor-copy'
import { useNewWatchdogMatches } from '../../../../features/investor-assistant/hooks/use-investor-assistant'

export interface SearchParams {
  q?: string
  chip?: Exclude<SearchChip, 'all'>
  sort?: 'name'
  open?: string
  /** The saved search to show (`?s=`); a search just made sets it too, so the answer survives a reload. */
  s?: string
  /** The view: the search (none) or the Discovery watchdog. */
  tab?: 'watchdog'
  /** Browse all startups, with the filters, instead of the search landing. */
  browse?: true
  /** Open the startup’s follow window at once (the watchdog’s Follow). */
  follow?: true
  /** Open the side panel with its note in edit mode (a result’s “Your notes”). */
  note?: true
}

export const Route = createFileRoute('/app/investor/$id/search')({
  component: SearchPage,
  // Leaving the page (Following, Settings) drops the session and the panel from the URL. Coming back reopens
  // the last ones, unless the URL already names a search, a panel, or a browse.
  beforeLoad: ({ params, search }) => {
    if (search.s || search.open || search.q || search.chip || search.browse) return
    const { s, open } = lastView('search', params.id)
    if (s || open)
      throw redirect({
        to: '/app/investor/$id/search',
        params,
        search: { ...search, s, open },
        replace: true,
      })
  },
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    ...(typeof search.q === 'string' && search.q.trim() && { q: search.q.trim().slice(0, 100) }),
    ...(search.sort === 'name' && { sort: 'name' as const }),
    ...((search.chip === 'week' ||
      search.chip === 'raising' ||
      search.chip === 'europe' ||
      search.chip === 'focus') && {
      chip: search.chip,
    }),
    ...(typeof search.open === 'string' && search.open && { open: search.open }),
    ...(typeof search.s === 'string' && search.s && { s: search.s.slice(0, 64) }),
    ...(search.tab === 'watchdog' && { tab: 'watchdog' as const }),
    ...((search.follow === true || search.follow === 'true') && { follow: true as const }),
    ...((search.note === true || search.note === 'true') && { note: true as const }),
    ...((search.browse === true || search.browse === 'true' || search.browse === '1') && {
      browse: true as const,
    }),
  }),
})

function SearchPage() {
  const { q, chip, sort, open, s, tab, browse, follow, note } = Route.useSearch()
  // The landing is the composer; browsing (or a filter in the URL) shows the full list instead.
  const browsing = Boolean(browse || q || chip)
  const newMatches = useNewWatchdogMatches()
  const { id: investorId } = Route.useParams()
  const navigate = useNavigate({ from: Route.fullPath })
  // Set when the panel opens straight into the follow window (a pass window's "Follow instead").
  const [followFirst, setFollowFirst] = useState<string | null>(null)
  // The passage a search quoted for the startup the panel shows; not in the URL, so a reload leaves it out.
  const [matched, setMatched] = useState<MatchedPassage | null>(null)
  // Passes made or undone in the search or its side panel, by startup: the rows and the panel read the same record.
  const [passes, setPasses] = useState<Record<string, boolean>>({})
  const setPassed = (startupId: string, passed: boolean) =>
    setPasses((prev) => ({ ...prev, [startupId]: passed }))
  // Follows started or stopped in the search or its side panel, by startup, as the pass record above.
  const [follows, setFollows] = useState<Record<string, boolean>>({})
  const setFollowed = (startupId: string, following: boolean) =>
    setFollows((prev) => ({ ...prev, [startupId]: following }))
  const set = (next: Partial<SearchParams>) => {
    rememberView('search', investorId, {
      ...('s' in next && { s: next.s }),
      ...('open' in next && { open: next.open }),
    })
    return navigate({ search: (prev) => ({ ...prev, ...next }) })
  }
  // A new search starts the phrase search afresh: its text, answer and pending state go with the old one.
  const [session, setSession] = useState(0)
  const newSearch = () => {
    setSession((n) => n + 1)
    setPasses({})
    setFollows({})
    set({ s: undefined })
  }
  const closePanel = () => {
    setFollowFirst(null)
    set({ open: undefined })
  }

  return (
    <AssistantGate
      activeNav={INVESTOR_NAV_LABEL.search}
      sidePanel={
        open ? (
          <StartupSidePanel
            key={note ? `${open}:note` : open}
            writeNote={note === true}
            startupId={open}
            from="search"
            matched={matched}
            onPassChange={setPassed}
            onFollowChange={setFollowed}
            startFollow={followFirst === open || follow === true}
            onClose={closePanel}
          />
        ) : undefined
      }
    >
      {/* The prototype's search column is 768px; the browse table needs the wider one. */}
      <div className={`mx-auto space-y-8 py-2 pb-10 ${browsing ? 'max-w-5xl' : 'max-w-3xl'}`}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
              {copy('search.title')}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">{copy('search.subtitle')}</p>
          </div>
          {s && (
            <SecondaryButton
              type="button"
              size="sm"
              icon={<Plus />}
              className="shrink-0"
              onClick={newSearch}
            >
              {copy('search.new')}
            </SecondaryButton>
          )}
        </div>
        <nav aria-label={copy('search.tabs.label')} className="flex gap-6 border-b border-border">
          <button
            type="button"
            aria-current={tab ? undefined : 'page'}
            onClick={() => set({ tab: undefined })}
            className={`-mb-px border-b-2 pb-2.5 text-sm ${tab ? 'border-transparent text-muted-foreground hover:text-foreground' : 'border-foreground font-medium text-foreground'}`}
          >
            {copy('search.tabs.search')}
          </button>
          <button
            type="button"
            aria-current={tab === 'watchdog' ? 'page' : undefined}
            onClick={() => set({ tab: 'watchdog' })}
            className={`-mb-px border-b-2 pb-2.5 text-sm ${tab === 'watchdog' ? 'border-foreground font-medium text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          >
            {copy('watchdog.tab')}
            {newMatches > 0 && (
              <span className="ml-2 text-xs font-medium text-primary">
                {copy('watchdog.new', { n: newMatches })}
              </span>
            )}
          </button>
        </nav>
        {tab === 'watchdog' ? (
          <WatchdogTab />
        ) : browsing ? (
          <BrowseSection
            investorId={investorId}
            q={q}
            chip={chip ?? 'all'}
            sort={sort ?? 'joined'}
            openId={open ?? null}
            onSort={(next) => set({ sort: next === 'name' ? 'name' : undefined })}
            onQuery={(next) => set({ q: next })}
            onChip={(next) => set({ chip: next === 'all' ? undefined : next })}
            onClearFilters={() => set({ q: undefined, chip: undefined })}
            onOpen={(startupId) => set({ open: startupId })}
          />
        ) : (
          <>
            <PhraseSearch
              key={session}
              searchId={s}
              passes={passes}
              onPassChange={setPassed}
              follows={follows}
              onFollowChange={setFollowed}
              onAnswered={(searchId) => set({ s: searchId })}
              onEditFocus={() => set({ tab: 'watchdog' })}
              onBrowse={() => set({ browse: true })}
              onClear={newSearch}
              onOpen={(startupId, startFollow, passage) => {
                setFollowFirst(startFollow ? startupId : null)
                setMatched(passage ?? null)
                set({ open: startupId, note: undefined })
              }}
              onWriteNote={(startupId) => {
                setFollowFirst(null)
                setMatched(null)
                set({ open: startupId, note: true })
              }}
            />
            <RecentSearches openSearchId={s} onPick={(searchId) => set({ s: searchId })} />
            <ConnectAiLink investorId={investorId} />
          </>
        )}
      </div>
    </AssistantGate>
  )
}
